// THE CLIENT WORKER POOL (R8c1): ONE worker family for the client's CPU work
// off the main thread. R8c1 ships the pool with test ops only (poolwork.js
// echo / wasm.sum) and no consumer; R8c2+ move work onto it.
//
//   run(op, args, { transfer, affinity, prio, wasm }) -> Promise of the op's result
//     op        a name in the registry (poolwork.js OPS: pure functions, the
//               same ones on both sides)
//     transfer  the transferables in `args`, moved (detached here) with the
//               job; the op names its own back (its result's buffers)
//     affinity  a key: the job runs on the worker the key first ran on, for
//               ever (state an op keeps per worker, R8c2+); none: any idle one
//     prio      assetq.js's lanes: 'map' > 'near' > 'bulk' (the default);
//               queued jobs start in prio order, FIFO within a prio
//     wasm      an artefact name (wasm.js): the job runs on its instance; the
//               compiled Module goes with the first job of each worker that
//               names it (the worker instantiates and keeps it); the job
//               queues once the Module is compiled
//
// MAP FIRST: nothing starts before heavyReady (sched.js) — a run() asked
// earlier waits; then workers start lazily (one more while a job waits and the
// pool is under its size), each runs one job at a time.
// A worker's failure (onerror: the worker itself, never an op's throw — that
// is the job's {error}) rejects its in-flight job with that error and marks it
// dead; the pool replaces it ONCE; the replacement dying too fails the pool
// loud: every job, queued or later, rejects. A replaced worker starts empty
// (no wasm instance, no per-key state); its pinned keys stay pinned to it.
// Without a Worker (node, the dashboard bundle: no __POOL_WORKER_SRC__ / no
// Worker) each "worker" runs poolwork.js handle() in-realm, async, its
// messages structured-cloned with the same transfer lists: one code path.
// The main-thread side is labelled act('pool.<op>') at the result hand-off
// (the caller's continuation, sched.js long-task attribution).
import { heavyReady, act } from './sched.js';
import { deviceClass } from './device.js';
import { OPS, handle } from './poolwork.js';
import { wasmModule } from './wasm.js';
import { setGroundPrep } from './groundprep.js';

// POOL SIZE by device class: desktop-* = min(4, max(1, cores - 1)) (one core
// left to the page), the rest 2. A CLIENT constant, never renderconfig (a
// renderconfig term moves the bake id). No class yet / unknown: 2.
const DESKTOP = (hc) => Math.min(4, Math.max(1, (hc | 0) - 1));
export const POOL_SIZE = Object.freeze({
  'desktop-dgpu': DESKTOP, 'desktop-igpu': DESKTOP,
  quest: () => 2, 'phone-hi': () => 2, 'phone-lo': () => 2, software: () => 2,
});
export function poolSize(cls, hc) {
  return Object.prototype.hasOwnProperty.call(POOL_SIZE, cls) ? POOL_SIZE[cls](hc) : 2;
}

const PRIO = { map: 0, near: 1, bulk: 2 };
// a structured clone with its transfer list (the post's semantics, in-realm)
const clone = (v, transfer) => (typeof structuredClone === 'function' ? structuredClone(v, { transfer }) : v);

// an in-realm "worker": poolwork.js handle() on a microtask, the same message shapes
function localWorker() {
  const cache = new Map();
  const w = {
    onmessage: null, onerror: null, gone: false,
    postMessage(msg, transfer = []) {
      const mod = msg.wasm && msg.wasm.module;                 // a Module is shared, not cloned
      const m = clone({ ...msg, wasm: msg.wasm ? { name: msg.wasm.name } : undefined }, transfer);
      if (mod) m.wasm.module = mod;
      Promise.resolve().then(() => handle(m, cache)).then(({ msg: out, transfer: t }) => {
        if (!w.gone && w.onmessage) w.onmessage({ data: clone(out, t) });
      });
    },
    terminate() { w.gone = true; },
  };
  return w;
}

// size: n | () => n (read at the start); spawn: () => a Worker (postMessage /
// onmessage / onerror / terminate) | null (in-realm); ready: () => promise
// (nothing starts before it); wasm: name -> Promise<WebAssembly.Module>
export function createPool({ size = 2, spawn = () => null, ready = () => null, wasm = wasmModule } = {}) {
  const queue = [];                   // jobs not sent: {id, op, args, transfer, affinity, p, seq, art, mod, wait, res, rej}
  const slots = [];                   // {i, w, job, sent: Set(artefact), replaced}
  const pins = new Map();             // affinity -> slot index
  let n = 0, open = false, started = null, fatal = null, ids = 0, seq = 0;

  function fail(e, quiet = false) {
    if (fatal) return;
    fatal = e;
    if (!quiet) console.error(e);
    for (const j of queue.splice(0)) j.rej(e);
    for (const s of slots) {
      if (s.job) { s.job.rej(e); s.job = null; }
      try { s.w.terminate(); } catch { /* gone */ }
    }
  }

  function die(s, w, err) {
    if (s.w !== w || fatal) return;                           // a replaced worker's late event
    try { w.terminate(); } catch { /* gone */ }
    const j = s.job;
    s.job = null;
    s.sent = new Set();
    if (j) j.rej(err);
    if (s.replaced) { fail(new Error(`pool: worker ${s.i} died again after its replacement (${err.message})`)); return; }
    s.replaced = true;
    make(s);
    Promise.resolve().then(pump);                             // never inside a pump (make may run from slotFor)
  }

  function make(s) {
    let w;
    try { w = spawn() || localWorker(); } catch (e) {
      s.w = { terminate() {} };
      die(s, s.w, new Error(`pool: worker ${s.i} failed to start (${(e && e.message) || e})`));
      return;
    }
    s.w = w;
    w.onmessage = (e) => onMsg(s, w, e.data);
    w.onerror = (e) => {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      die(s, w, new Error(`pool: worker ${s.i} gone (${(e && e.message) || 'error'})`));
    };
  }

  function onMsg(s, w, d) {
    const j = s.job;
    if (s.w !== w || !j || !d || d.id !== j.id) return;       // not this worker's job
    s.job = null;
    if (d.error !== undefined) j.rej(new Error(`pool ${j.op}: ${d.error}`));
    else { act('pool.' + j.op); j.res(d.result); }             // from here: the caller's continuation
    pump();
  }

  function send(s, j) {
    const msg = { id: j.id, op: j.op, args: j.args };
    const first = j.art && !s.sent.has(j.art);
    if (j.art) msg.wasm = first ? { name: j.art, module: j.mod } : { name: j.art };
    s.job = j;
    try { s.w.postMessage(msg, j.transfer); } catch (e) { s.job = null; j.rej(e); return; }   // DataCloneError: the caller's
    if (first) s.sent.add(j.art);
    j.args = j.transfer = j.mod = null;
  }

  // the worker for j now (pinned, else an idle one, else a new one under the size) or null
  function slotFor(j) {
    if (j.affinity != null && pins.has(j.affinity)) { const s = slots[pins.get(j.affinity)]; return s.job ? null : s; }
    let s = slots.find((x) => !x.job) || null;
    if (!s && slots.length < n) { s = { i: slots.length, w: null, job: null, sent: new Set(), replaced: false }; slots.push(s); make(s); }
    if (s && fatal) return null;
    if (s && j.affinity != null) pins.set(j.affinity, s.i);
    return s;
  }

  function pump() {
    if (!open || fatal) return;
    queue.sort((a, b) => a.p - b.p || a.seq - b.seq);
    for (let k = 0; k < queue.length; k++) {
      const j = queue[k];
      if (j.wait) continue;
      const s = slotFor(j);
      if (!s) continue;
      queue.splice(k--, 1);
      send(s, j);
    }
  }

  function run(op, args, { transfer = [], affinity = null, prio = 'bulk', wasm: art = null } = {}) {
    if (fatal) return Promise.reject(fatal);
    if (!Object.prototype.hasOwnProperty.call(OPS, op)) return Promise.reject(new Error(`pool: no op ${op}`));
    return new Promise((res, rej) => {
      const j = { id: ++ids, op, args, transfer, affinity, p: PRIO[prio] ?? PRIO.bulk, seq: seq++, art, mod: null, wait: !!art, res, rej };
      if (!started) {
        started = Promise.resolve(ready()).then(() => {
          n = Math.max(1, (typeof size === 'function' ? size() : size) | 0);
          open = true;
          pump();
        });
      }
      queue.push(j);
      if (art) {
        started.then(() => wasm(art)).then((m) => { j.mod = m; j.wait = false; pump(); }, (e) => {
          const k = queue.indexOf(j);
          if (k >= 0) { queue.splice(k, 1); rej(e); }
        });
      }
      pump();
    });
  }

  // stop every worker; queued and in-flight jobs reject (the gates' tear-down)
  const close = () => fail(new Error('pool: closed'), true);
  const stats = () => ({ size: n, open, workers: slots.length, busy: slots.filter((s) => s.job).length, queued: queue.length, fatal: fatal ? fatal.message : null });
  return { run, close, stats };
}

/* global __POOL_WORKER_SRC__ */
// the browser's worker: poolwork.js inlined by scripts/build-client.js; null
// (in-realm) without the bundle's define or a Worker
let _src = null;
function spawnWorker() {
  if (typeof __POOL_WORKER_SRC__ === 'undefined' || typeof Worker === 'undefined') return null;
  if (!_src) _src = URL.createObjectURL(new Blob([__POOL_WORKER_SRC__], { type: 'text/javascript' }));
  return new Worker(_src);
}
const cores = () => (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 0;

const POOL = createPool({ size: () => poolSize(deviceClass(), cores()), spawn: spawnWorker, ready: heavyReady, wasm: wasmModule });
export const run = (op, args, opts) => POOL.run(op, args, opts);
// ClientGround's off-thread index (groundprep.js; a copy of the bytes: the caller's buffer may be another reader's body)
setGroundPrep((b) => { const ab = (b instanceof Uint8Array ? b : new Uint8Array(b)).slice().buffer; return run('ground.index', { ab }, { transfer: [ab], wasm: 'vrm_core', prio: 'near' }).then((r) => new Uint8Array(r.ix)); });
export const poolStats = () => POOL.stats();
